import { Activity, ArrowRight, Minus, Play, Plus, RotateCcw, X } from 'lucide-react';
import { useEffect, useRef, useState, type ComponentProps, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { KanameGlyph } from '@/components/brand/KanameGlyph';
import { KanameIntro, type IntroPhase } from '@/components/brand/KanameIntro';
import {
  KanameMark,
  type MarkCue,
  type MarkGesture,
  type MarkRhythm,
  type MarkTone,
} from '@/components/brand/KanameMark';
import { KanameNote } from '@/components/brand/KanameNote';
import { KanameWordmark } from '@/components/brand/KanameWordmark';
import { LeafGround } from '@/components/brand/LeafGround';
import type { MarkStateKind } from '@/components/brand/markStates';
import { FAN_FULL, FAN_LOCKED, FAN_MISSING, fanOf, petalsFor, type Petals } from '@/components/brand/petals';
import { PetalSwatch, ServiceFan, type FanPlate } from '@/components/brand/ServiceFan';
import { ServiceFanStage, type FanAsideApi } from '@/components/brand/ServiceFanStage';
import { StateMark } from '@/components/brand/StateMark';
import { useGust } from '@/components/brand/useGust';
import { AccountMenu } from '@/components/layout/shell/AccountMenu';
import type { AccountInfo } from '@/components/layout/shell/types';
import type { HomeGesture } from '@/components/observer/observerContext';
import { EVENT_PLAN, HOME_GESTURE_MS, SUCCESS_HOLD_MS } from '@/components/observer/observerEvents';
import {
  Avatar,
  Badge,
  Button,
  IconButton,
  Kbd,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsList,
  TabsTrigger,
  cn,
  type AvatarSize,
} from '@/components/ui';
import { plural } from '@/lib/plural';
import { Code, Section, SubTitle } from './Section';

/*
  Знак и бренд (R41, docs/brand-concept.md) — плитки всего, что выросло из знака: веер и его
  состояния (ожидание и ошибки R42, отклики ядра для помощника R36), дом помощника (ядро в знаке и
  выделение пункта: тон, ожидание, отлучка, отклик), веер сервисов «Мои сервисы»
  (R43, spec-4), слово KANAME, сноска, значок меню, знакомство на входе, листья на земле, порыв ветра.
  Лепестки светлые, поэтому образцы стоят на тёмной сцене (bg-night) в обеих темах; значок и слово
  в строке берут цвет от текста и показаны прямо на карточке. Полёт ядра в выделение живёт только
  в настоящем меню — ссылка на «Основу».
*/

function Row({ title, note, children }: { title: string; note?: ReactNode; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-line bg-card p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <SubTitle>{title}</SubTitle>
        {note && <span className="text-xs text-fg-muted">{note}</span>}
      </div>
      {children}
    </div>
  );
}

/** Тёмная сцена: лепестки светлые и на светлой карточке пропадают. */
function Stage({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn('rounded-md bg-night p-6 text-petal', className)}>{children}</div>;
}

/**
 * Образец на сцене с подписью-кодом под ним. Код переносится только по строкам из \n, пояснение —
 * отдельной строкой: выражение не рвётся посередине.
 */
function Sample({ code, note, children }: { code: string; note?: string; children: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-3">
      {children}
      <span className="flex flex-col items-center gap-1 text-center text-2xs text-petal/60">
        <code className="font-mono whitespace-pre-line">{code}</code>
        {note && <span>{note}</span>}
      </span>
    </div>
  );
}

type MarkProps = ComponentProps<typeof KanameMark>;

/** Состояния знака без движения. */
const STATES: { code: string; props: MarkProps }[] = [
  { code: 'open', props: {} },
  { code: 'open={false}', props: { open: false } },
  { code: 'core="away"', props: { core: 'away' } },
  { code: 'core="none"', props: { core: 'none' } },
  { code: 'variant="window"', props: { variant: 'window' } },
  { code: 'variant="window" leaves', props: { variant: 'window', leaves: true } },
];

/** Знак живёт сам: дыхание ядра, дыхание веера, поворот на лепесток. */
const LIVES: { code: string; props: MarkProps }[] = [
  { code: 'breathing', props: { breathing: true } },
  { code: 'life="breath"', props: { life: 'breath', breathing: true } },
  { code: 'life="turn"', props: { life: 'turn' } },
  { code: 'life="both" — в меню', props: { life: 'both', breathing: true } },
];

function MarkPlay() {
  const [open, setOpen] = useState(true);
  const [windowed, setWindowed] = useState(false);
  return (
    <div className="flex flex-col gap-3">
      <Stage className="grid flex-1 grid-cols-2 gap-6">
        {(['quick', 'slow'] as const).map((tempo) => (
          <Sample key={tempo} code={`tempo="${tempo}"`}>
            <KanameMark
              open={open}
              tempo={tempo}
              variant={windowed ? 'window' : 'solid'}
              className="size-24"
            />
          </Sample>
        ))}
      </Stage>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="secondary" onClick={() => setOpen((o) => !o)}>
          {open ? 'Сложить' : 'Раскрыть'}
        </Button>
        <Button size="sm" variant="secondary" onClick={() => setWindowed((w) => !w)}>
          {windowed ? 'Сплошные' : 'Окна'}
        </Button>
      </div>
    </div>
  );
}

/* ---------- Состояния знака (R42, помощник R36): petals · reveal · tone · rhythm · cue ---------- */

/** Веер-состояние без движения: полный, пропуск, погашенная пластина, загрузка на шаге 3 из 6. */
const FANS: { code: string; note: string; petals: Petals }[] = [
  { code: 'FAN_FULL', note: 'полный', petals: FAN_FULL },
  { code: 'FAN_MISSING', note: 'не найдено', petals: FAN_MISSING },
  { code: 'FAN_LOCKED', note: 'нет доступа', petals: FAN_LOCKED },
  { code: 'fanOf(3)', note: 'шаг 3 из 6', petals: fanOf(petalsFor(3, 6)) },
];

const MARK_TONES: { value: MarkTone; label: string }[] = [
  { value: 'brand', label: 'Покой' },
  { value: 'info', label: 'Операция' },
  { value: 'success', label: 'Готово' },
  { value: 'warning', label: 'Нет связи' },
  { value: 'danger', label: 'Сбой' },
];

const RHYTHMS: { value: MarkRhythm; label: string; note: string }[] = [
  { value: 'calm', label: 'Спокойно', note: 'дышит раз в 3,6 с' },
  { value: 'quick', label: 'Чаще', note: 'вдвое чаще — сбой' },
  { value: 'still', label: 'Замер', note: 'затаил дыхание — нет связи' },
  { value: 'await', label: 'Ждёт', note: 'двойной удар — ждёт ответа' },
];

const GESTURES: { value: MarkGesture; label: string }[] = [
  { value: 'alarm', label: 'Тревога' },
  { value: 'burst', label: 'Кольцо' },
  { value: 'bounce', label: 'Прыжок' },
  { value: 'shake', label: 'Встряска' },
  { value: 'shiver', label: 'Брр' },
  { value: 'flash', label: 'Вспышка' },
  { value: 'pulse', label: 'Пульс' },
];

/** Переключатель-сегменты галереи (как на верхней полосе «Основы»). */
function Segmented<T extends string>({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-2xs font-medium text-fg-subtle uppercase">{label}</span>
      {/* На узкой карточке сегменты не переносятся (таблетка ездит только вдоль), а прокручиваются. */}
      <Tabs
        variant="pill"
        value={value}
        onValueChange={(v) => onChange(v as T)}
        className="max-w-full overflow-x-auto"
      >
        <TabsList className="w-max">
          {options.map((o) => (
            <TabsTrigger key={o.value} value={o.value}>
              {o.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
    </div>
  );
}

const STEPS = 6;

/** Знак-загрузчик: один лепесток — один пройденный шаг; шестой выходит только на «готово». */
function StepDemo() {
  const [done, setDone] = useState(1);
  const [gust, setGust] = useState(0);
  const ready = done >= STEPS;
  const petals = fanOf(petalsFor(done, STEPS));
  const reveal = ready ? 'snap' : 'step';
  return (
    <div className="flex flex-col gap-3">
      <Stage className="flex flex-1 flex-wrap items-center justify-center gap-10">
        <Sample code={`reveal="${reveal}" · size-40`}>
          <KanameMark petals={petals} reveal={reveal} breathing gust={gust} className="size-40" />
        </Sample>
        <Sample code="size-16">
          <KanameMark petals={petals} reveal={reveal} breathing gust={gust} className="size-16" />
        </Sample>
      </Stage>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant="secondary"
          disabled={done >= STEPS - 1}
          onClick={() => setDone((d) => d + 1)}
        >
          + шаг
        </Button>
        <Button size="sm" variant="secondary" disabled={ready} onClick={() => setDone(STEPS)}>
          Готово
        </Button>
        <Button size="sm" variant="ghost" leftIcon={<RotateCcw />} onClick={() => setDone(0)}>
          Заново
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setGust((g) => g + 1)}>
          Порыв
        </Button>
        <span className="text-xs text-fg-muted tabular-nums">
          {ready ? 'Готово — досадка' : `Шаг ${done} из ${STEPS} · лепестков ${petalsFor(done, STEPS)}`}
        </span>
      </div>
    </div>
  );
}

/** Тон, ритм и отклики ядра: один и тот же ход у ядра дома и у кольца ушедшего ядра. */
function CueDemo() {
  const [tone, setTone] = useState<MarkTone>('brand');
  const [rhythm, setRhythm] = useState<MarkRhythm>('calm');
  const [cue, setCue] = useState<MarkCue | null>(null);
  return (
    <div className="flex flex-col gap-3">
      <Stage className="flex flex-wrap items-center justify-center gap-10">
        <Sample code="core home">
          <KanameMark tone={tone} rhythm={rhythm} cue={cue} breathing life="breath" className="size-24" />
        </Sample>
        <Sample code='core="away"'>
          <KanameMark tone={tone} cue={cue} core="away" className="size-24" />
        </Sample>
      </Stage>
      <div className="flex flex-col gap-3">
        <Segmented label="Тон" value={tone} onChange={setTone} options={MARK_TONES} />
        <Segmented label="Ритм" value={rhythm} onChange={setRhythm} options={RHYTHMS} />
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-2xs font-medium text-fg-subtle uppercase">Отклик</span>
          {GESTURES.map((g) => (
            <Button
              key={g.value}
              size="sm"
              variant="secondary"
              onClick={() => setCue((c) => ({ gesture: g.value, at: (c?.at ?? 0) + 1 }))}
            >
              {g.label}
            </Button>
          ))}
        </div>
        <span className="text-xs text-fg-muted">
          {cue ? <Code>{`cue={{ gesture: '${cue.gesture}', at: ${cue.at} }}`}</Code> : 'Отклика ещё не было.'}{' '}
          Тревога — два удара ядра и красное кольцо; повтор чаще раза в 1,5 с схлопывается; у ушедшего ядра
          звучит только кольцо. Тон проявляется с темпом срочности: сбой скачком, возврат в покой за 0,8 с.
        </span>
      </div>
    </div>
  );
}

function MarkStatesDemo() {
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="flex flex-col gap-3">
          <Stage className="grid flex-1 grid-cols-2 content-center gap-6 xl:grid-cols-4">
            {FANS.map((f) => (
              <Sample key={f.code} code={f.code} note={f.note}>
                <KanameMark petals={f.petals} className="size-16" />
              </Sample>
            ))}
          </Stage>
          <span className="text-xs text-fg-muted">
            Место «слова» одно — лепесток на 12 часах: пропуск — пунктирный контур, нет доступа — пластина
            погашена. Неполный веер не поворачивается, порыв доводит его целыми оборотами.
          </span>
        </div>
        <StepDemo />
      </div>

      <Stage className="grid grid-cols-2 gap-6 sm:grid-cols-3 lg:grid-cols-5">
        {MARK_TONES.map((t) => (
          <Sample key={`home-${t.value}`} code={`tone="${t.value}"`}>
            <KanameMark tone={t.value} className="size-16" />
          </Sample>
        ))}
        {MARK_TONES.map((t) => (
          <Sample key={`away-${t.value}`} code={`core="away"\ntone="${t.value}"`}>
            <KanameMark tone={t.value} core="away" className="size-16" />
          </Sample>
        ))}
      </Stage>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="flex flex-col gap-3">
          <Stage className="grid flex-1 grid-cols-2 content-center gap-6 xl:grid-cols-4">
            {RHYTHMS.map((r) => (
              <Sample key={r.value} code={`rhythm="${r.value}"`} note={r.note}>
                <KanameMark rhythm={r.value} breathing life="breath" className="size-16" />
              </Sample>
            ))}
          </Stage>
          <span className="text-xs text-fg-muted">
            Все ритмы — по общим часам дыхания: «чаще» ровно вдвое, «ждёт» — двойной удар в такт веера.
            «Замер» останавливает дыхание и поворот на месте, отклики при этом звучат.
          </span>
        </div>
        <CueDemo />
      </div>
    </div>
  );
}

/* ---------- Состояния знака — ожидание и ошибки (R42): StateMark · MARK_STATES ---------- */

/** Шаги образца первой загрузки — одни на медальоне и на сцене. */
const BOOT_STEPS = ['Kaname', 'Вход', 'Профиль и права', 'Сервисы и меню', 'Вкладки', 'Первый экран'];
const BOOT_TOTAL = BOOT_STEPS.length;
/** Темп «проиграть» — как «Медленно» в «Основе»: шаг за 600 мс. */
const PLAY_STEP_MS = 600;

interface StateFrameSample {
  kind: MarkStateKind;
  done?: number;
}

/** Образец состояния: кадры хода, последний — поза покоя. */
interface StateSample {
  label: string;
  frames: StateFrameSample[];
}

const stepsTo = (to: number): StateFrameSample[] =>
  Array.from({ length: to + 1 }, (_, done) => ({ kind: 'loading', done }));

/** Медальон во вкладке: первое за сеанс открытие приложения и состояния вкладки целиком. */
const MEDALLION_SAMPLES: StateSample[] = [
  { label: 'Сборка 3/6', frames: stepsTo(3) },
  { label: 'Не найдено', frames: [{ kind: 'missing' }] },
  { label: 'Нет доступа', frames: [{ kind: 'locked' }] },
  {
    label: 'Не открылось',
    frames: [
      { kind: 'loading', done: 3 },
      { kind: 'failed', done: 3 },
    ],
  },
  { label: 'Сбой вкладки', frames: [{ kind: 'loading', done: BOOT_TOTAL }, { kind: 'crashed' }] },
];

/** Сцена — где оболочки ещё нет (первая загрузка) или уже нет (падение интерфейса): ядро живое. */
const SCENE_SAMPLES: StateSample[] = [
  { label: 'Сборка 3/6', frames: stepsTo(3) },
  {
    label: 'Не открылось',
    frames: [
      { kind: 'loading', done: 3 },
      { kind: 'failed', done: 3 },
    ],
  },
  {
    label: 'Нет связи',
    frames: [
      { kind: 'loading', done: 3 },
      { kind: 'offline', done: 3 },
    ],
  },
  { label: 'Падение', frames: [{ kind: 'crashed' }] },
];

/** Одно состояние: знак в покое и «Проиграть» — ход заново, с перемонтажом (без прошлых поз). */
function StateCell({ sample, setting }: { sample: StateSample; setting: 'medallion' | 'scene' }) {
  const last = sample.frames.length - 1;
  const [run, setRun] = useState({ key: 0, at: last });
  useEffect(() => {
    if (run.at >= last) return undefined;
    const t = window.setTimeout(() => setRun((r) => ({ ...r, at: r.at + 1 })), PLAY_STEP_MS);
    return () => window.clearTimeout(t);
  }, [run, last]);
  const f = sample.frames[run.at];
  const scene = setting === 'scene';
  const kind = sample.frames[last].kind;
  return (
    <div className="flex flex-col items-center gap-2">
      <div className={cn('grid place-items-center', scene ? 'size-40' : 'size-24')}>
        <StateMark key={run.key} kind={f.kind} done={f.done} total={BOOT_TOTAL} setting={setting} />
      </div>
      <span className={cn('text-xs font-medium', scene ? 'text-petal' : 'text-fg')}>{sample.label}</span>
      <code className={cn('font-mono text-2xs', scene ? 'text-petal/60' : 'text-fg-muted')}>
        {`kind="${kind}"`}
      </code>
      <Button
        size="sm"
        variant="ghost"
        leftIcon={<Play />}
        aria-label={`Проиграть «${sample.label}»`}
        onClick={() => setRun((r) => ({ key: r.key + 1, at: 0 }))}
      >
        Проиграть
      </Button>
    </div>
  );
}

/** а) Словарь MARK_STATES в двух видах: медальон 64 px на карточке и сцена 160 px на bg-night. */
function StateGrid() {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-4 rounded-md border border-line bg-canvas p-5">
        <span className="text-2xs font-medium text-fg-subtle uppercase">
          Медальон во вкладке · знак 64 px
        </span>
        <div className="grid grid-cols-2 gap-6 sm:grid-cols-3 lg:grid-cols-5">
          {MEDALLION_SAMPLES.map((s) => (
            <StateCell key={s.label} sample={s} setting="medallion" />
          ))}
        </div>
      </div>
      {/* Сцена — тёмный остров в обеих темах: кнопки на ней — в тёмной теме. */}
      <div data-theme="dark" className="flex flex-col gap-4 rounded-md bg-night p-5">
        <span className="text-2xs font-medium text-petal/60 uppercase">Сцена · знак 160 px · живое ядро</span>
        <div className="grid grid-cols-2 gap-6 lg:grid-cols-4">
          {SCENE_SAMPLES.map((s) => (
            <StateCell key={s.label} sample={s} setting="scene" />
          ))}
        </div>
      </div>
      <span className="text-xs text-fg-muted">
        Лепесток на 12 часах — место «слова»: не найдено — пунктирный контур приходит в такт пропущенной
        пластины, нет доступа — пластина выходит и гаснет. Сбой — веер стоит на достигнутом, кольцо медальона
        скачком краснеет и становится толще; на сцене ядро даёт тревогу и дышит вдвое чаще, а при падении
        интерфейса останавливается. Нет связи — веер затаил дыхание. Живое ядро — только на сцене: во вкладке
        оно одно, в меню (R41).
      </span>
    </div>
  );
}

/** б) Один и тот же ход на 64 и 160 px: шаги, «готово», сбой, нет связи. */
function StatePlayer() {
  const [s, setS] = useState<{ key: number; kind: MarkStateKind; done: number }>({
    key: 0,
    kind: 'loading',
    done: 1,
  });
  const ready = s.kind === 'loading' && s.done >= BOOT_TOTAL;
  const line =
    s.kind === 'failed'
      ? `Не удалось: ${BOOT_STEPS[s.done].toLowerCase()} · сервер не ответил за 10 с`
      : s.kind === 'offline'
        ? 'Нет связи — продолжим, когда появится'
        : ready
          ? 'Готово — досадка'
          : `Шаг ${s.done + 1} из ${BOOT_TOTAL} · ${BOOT_STEPS[s.done]}`;
  return (
    <div className="flex flex-col gap-3">
      <div className="grid gap-4 md:grid-cols-2">
        <div className="grid place-items-center rounded-md border border-line bg-canvas p-6">
          <StateMark key={s.key} kind={s.kind} done={s.done} total={BOOT_TOTAL} />
        </div>
        <div className="grid place-items-center rounded-md bg-night p-6">
          <StateMark key={s.key} kind={s.kind} done={s.done} total={BOOT_TOTAL} setting="scene" />
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant="secondary"
          leftIcon={<Plus />}
          disabled={s.done >= BOOT_TOTAL - 1}
          onClick={() => setS((p) => ({ ...p, kind: 'loading', done: p.done + 1 }))}
        >
          шаг
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={ready}
          onClick={() => setS((p) => ({ ...p, kind: 'loading', done: BOOT_TOTAL }))}
        >
          Готово
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={ready || s.kind === 'failed'}
          onClick={() => setS((p) => ({ ...p, kind: 'failed' }))}
        >
          Сбой
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={ready || s.kind === 'offline'}
          onClick={() => setS((p) => ({ ...p, kind: 'offline' }))}
        >
          Нет связи
        </Button>
        <Button
          size="sm"
          variant="ghost"
          leftIcon={<RotateCcw />}
          onClick={() => setS((p) => ({ key: p.key + 1, kind: 'loading', done: 1 }))}
        >
          Заново
        </Button>
        <span className="text-xs text-fg-muted tabular-nums">{line}</span>
      </div>
      <span className="text-xs text-fg-muted">
        Один лепесток — один пройденный шаг, шестой выходит только на «готово» — досадкой. После сбоя или
        обрыва «+ шаг» — это «Повторить»: тон уходит, шаги идут дальше с того же места. Ничего не крутится.
      </span>
    </div>
  );
}

/** г) Где знак, где заглушка (R42): знак ждёт за рамку, заглушка — за данные. */
const WHERE_ROWS: { what: string; where: string }[] = [
  {
    what: 'Знак по шагам',
    where: 'Первая загрузка сайта — сцена; первое за сеанс открытие приложения — медальон во вкладке',
  },
  {
    what: 'Знак-состояние',
    where:
      'Вкладка целиком: не найдено, нет доступа, не открылось, сбой вкладки — медальон; падение всего интерфейса — сцена',
  },
  {
    what: 'Skeleton той же формы',
    where:
      'Меню приложения, таблицы, панели, повторное открытие, разделы хаба (Marketplace, Настройки), данные внутри страницы',
  },
  { what: 'StepProgress', where: 'Операции человека над объектами — обслуживание host-338' },
  { what: 'Spinner', where: 'Только в кнопке и только на ожидание короче 1 с' },
  { what: 'Ничего', where: 'Ожидание короче 400 мс' },
  { what: 'EmptyState со значком в круге', where: 'Пустота внутри страницы — «Ничего не нашлось»' },
];

function WhereTable() {
  return (
    <div className="rounded-md border border-line">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-56">Что показываем</TableHead>
            <TableHead>Где</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {WHERE_ROWS.map((r) => (
            <TableRow key={r.what}>
              <TableCell className="font-medium text-fg">{r.what}</TableCell>
              <TableCell className="text-fg-muted">{r.where}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

/* ---------- ServiceFan — веер сервисов («Мои сервисы», spec-4) ---------- */

/** Имена образца: сервисов больше 36, чтобы «Подключить» работал и за пределом комфорта. */
const FAN_NAMES = [
  'Инфраструктура',
  'Slack',
  'Jira',
  'NetBox',
  'ИИ-сводка смены',
  'Пересмотр доступов',
  'GitLab',
  'Grafana',
  'Zabbix',
  'VictoriaMetrics',
  'Telegram',
  'OpenBao',
  'Proxmox VE',
  'BILLmanager',
  'DNSmanager',
  'VictoriaLogs',
  'Alertmanager',
  'Confluence',
  'incident.io',
  'Sentry',
  'ИИ-разбор алертов',
  'Дежурства',
  'Резервные копии',
  'Ёмкость и прогноз',
  'Ansible',
  'Объектное хранилище',
  'Почта',
  'Журнал действий',
  'ИИ-черновик ответа',
  'Terraform',
  'VMmanager',
  'Обратные зоны',
  'Сертификаты',
  'Страница статуса',
  'Сканер уязвимостей',
  'Очереди задач',
  'Планировщик работ',
  'Опросы клиентов',
  'Поиск по логам',
  'Склад запчастей',
];
const FAN_ALERT = 'Webhook не отвечает 12 мин';

/** «1 сервис», «3 сервиса», «12 сервисов». */
function servicesWord(n: number): string {
  return `${n} ${plural(n, 'сервис', 'сервиса', 'сервисов')}`;
}

/** Неподвижный образец: примерно каждая четвёртая контуром, у 12 и 30 — сбой у второй. */
function samplePlates(n: number): FanPlate[] {
  return FAN_NAMES.slice(0, n).map((label, k) => ({
    id: `s${k}`,
    label,
    on: k % 4 !== 3,
    alert: (n === 12 || n === 30) && k === 1 ? FAN_ALERT : undefined,
  }));
}

const FAN_SAMPLES = [0, 1, 3, 6, 7, 12, 24, 30];
const FAN_COUNTS = ['0', '6', '7', '12', '30', '36'] as const;

function FanSamples() {
  return (
    <div
      data-theme="dark"
      className="grid grid-cols-2 gap-6 rounded-md bg-night p-6 text-petal sm:grid-cols-4"
    >
      {FAN_SAMPLES.map((n) => (
        <Sample key={n} code={`N = ${n}`} note={n === 12 || n === 30 ? 'сбой у второй' : undefined}>
          <ServiceFan
            plates={samplePlates(n)}
            label={`Веер сервисов: ${servicesWord(n)}`}
            interactive={false}
            bloom="never"
            className="size-32"
          />
        </Sample>
      ))}
    </div>
  );
}

/** Карточка активной пластины в демо: имя, состояние, тумблер. */
function FanDemoCard({ api, onToggle }: { api: FanAsideApi; onToggle: (id: string) => void }) {
  const p = api.plate;
  if (!p) return null;
  return (
    <>
      <div className="flex h-8 items-center justify-between">
        <span className="type-overline text-fg-subtle">
          {api.index + 1} из {api.total}
        </span>
        {api.pinned && (
          <IconButton size="sm" variant="ghost" aria-label="К сводке" onClick={api.unpin}>
            <X />
          </IconButton>
        )}
      </div>
      <div className="flex items-center gap-3">
        <span className="min-w-0 flex-1 truncate type-subheading text-fg">{p.label}</span>
        {p.on && p.alert ? (
          <Badge variant="urgent" solid>
            Сбой
          </Badge>
        ) : (
          <Badge variant={p.on ? 'success' : 'neutral'} solid>
            {p.on ? 'Работает' : 'Выключен'}
          </Badge>
        )}
      </div>
      {p.on && p.alert && <p className="text-xs text-danger-fg">{p.alert}</p>}
      <div className="mt-auto border-t border-line pt-3">
        <div className="flex h-8 items-center justify-between gap-2">
          <span className="text-xs text-fg-muted">Включён</span>
          <Switch
            size="sm"
            data-card-primary=""
            aria-label="Включён"
            title="Выключенный сервис не шлёт событий; настройки сохраняются"
            checked={p.on}
            onCheckedChange={() => onToggle(p.id)}
          />
        </div>
      </div>
    </>
  );
}

/** Сводка в покое: легенда — она же счёт, сбой с «Показать», подсказка с клавишами. */
function FanDemoSummary({ api, plates }: { api: FanAsideApi; plates: readonly FanPlate[] }) {
  const on = plates.filter((p) => p.on).length;
  const alert = plates.find((p) => p.on && p.alert);
  return (
    <>
      <p className="type-heading text-fg">{servicesWord(plates.length)}</p>
      <div className="flex flex-col gap-1.5 text-sm text-fg-muted">
        <span className="flex items-center gap-2">
          <PetalSwatch on />
          включено {on}
        </span>
        {plates.length > on && (
          <span className="flex items-center gap-2">
            <PetalSwatch on={false} />
            выключено {plates.length - on}
          </span>
        )}
      </div>
      {alert && (
        <div className="flex min-w-0 items-center gap-2 text-xs">
          <Badge variant="urgent" solid>
            Сбой
          </Badge>
          <span className="min-w-0 truncate text-fg">{alert.label}</span>
          <Button variant="link" size="sm" className="ml-auto" onClick={() => api.show(alert.id)}>
            Показать
          </Button>
        </div>
      )}
      <div className="mt-auto flex flex-col gap-1.5">
        {plates.length > 36 && (
          <p className="type-caption text-fg-muted">
            Пластин больше 36 — они становятся уже. Позже сервисы можно будет собрать в группы по разделам
            Marketplace.
          </p>
        )}
        <p className="type-caption text-fg-muted">Наведите на пластину или пройдите стрелками</p>
        <p className="flex flex-wrap items-center gap-x-2 gap-y-1 type-caption text-fg-muted">
          <span className="inline-flex items-center gap-1 whitespace-nowrap">
            <Kbd>←</Kbd>
            <Kbd>→</Kbd>— по кругу
          </span>
          <span className="inline-flex items-center gap-1 whitespace-nowrap">
            <Kbd>Enter</Kbd>— к действиям
          </span>
          <span className="whitespace-nowrap">буква — к сервису</span>
        </p>
      </div>
    </>
  );
}

/** Живой веер: подключение (приход), отключение (уход), свет, пачки, сбой, секторы попадания. */
function FanDemo() {
  const [ids, setIds] = useState<string[]>(() => FAN_NAMES.slice(0, 7));
  const [off, setOff] = useState<ReadonlySet<string>>(() => new Set(['Пересмотр доступов']));
  const [alert, setAlert] = useState(true);
  const [sectors, setSectors] = useState(false);
  const [run, setRun] = useState(0);
  const plates: FanPlate[] = ids.map((id, k) => ({
    id,
    label: id,
    on: !off.has(id),
    alert: alert && k === 1 ? FAN_ALERT : undefined,
  }));
  const on = plates.filter((p) => p.on).length;
  const toggle = (id: string) =>
    setOff((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const connect = () => setIds((list) => [...list, FAN_NAMES.find((x) => !list.includes(x)) ?? list[0]]);
  const count = String(ids.length);
  return (
    <div className="flex flex-col gap-3">
      <ServiceFanStage
        key={run}
        plates={plates}
        label={`Мои сервисы: ${plates.length}, включено ${on}`}
        bloom={run > 0 ? 'always' : 'never'}
        debugSectors={sectors}
        fit="width"
        aside={(api) =>
          api.plate ? (
            <FanDemoCard api={api} onToggle={toggle} />
          ) : (
            <FanDemoSummary api={api} plates={plates} />
          )
        }
        empty={
          <div className="flex h-full flex-col justify-center gap-3">
            <p className="type-heading text-petal">Заклёпка без пластин — ещё не веер</p>
            <p className="text-sm text-petal/70">
              Подключите сервис — он станет первой пластиной этого веера: включённый светлый, выключенный
              контуром.
            </p>
            <div>
              <Button size="sm" onClick={connect}>
                Подключить сервис
              </Button>
            </div>
          </div>
        }
      />
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant="secondary"
          leftIcon={<Plus />}
          disabled={ids.length >= FAN_NAMES.length}
          onClick={connect}
        >
          Подключить
        </Button>
        <Button
          size="sm"
          variant="secondary"
          leftIcon={<Minus />}
          disabled={ids.length === 0}
          onClick={() => setIds((list) => list.slice(0, -1))}
        >
          Отключить последний
        </Button>
        <Button size="sm" variant="secondary" disabled={ids.length < 3} onClick={() => toggle(ids[2])}>
          Вкл/выкл третий
        </Button>
        <Button size="sm" variant="ghost" leftIcon={<RotateCcw />} onClick={() => setRun((r) => r + 1)}>
          Раскрыть заново
        </Button>
        <span className="text-xs text-fg-muted tabular-nums">
          Сервисов: {ids.length} · включено {on}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-6">
        <Segmented
          label="Сервисов"
          value={(FAN_COUNTS as readonly string[]).includes(count) ? count : ''}
          onChange={(v) => setIds(FAN_NAMES.slice(0, Number(v)))}
          options={FAN_COUNTS.map((c) => ({ value: c, label: c }))}
        />
        <Switch size="sm" label="Сбой" checked={alert} onCheckedChange={setAlert} />
        <Switch size="sm" label="Секторы попадания" checked={sectors} onCheckedChange={setSectors} />
      </div>
    </div>
  );
}

function WordmarkDemo() {
  // Перезапуск — через смену key: буквы выходят заново вместе со своими лепестками.
  const [run, setRun] = useState(0);
  return (
    <div className="flex flex-col gap-3">
      <Stage className="flex flex-col items-center gap-8 py-10">
        <KanameWordmark key={run} size="stage" play={run > 0 ? 'play' : 'still'} />
        <span className="flex items-center gap-3">
          <KanameMark className="size-8" />
          <KanameWordmark />
        </span>
      </Stage>
      <div className="flex flex-wrap items-center gap-6">
        <span className="text-fg">
          <KanameWordmark />
        </span>
        <span className="text-fg-muted">
          <KanameWordmark />
        </span>
        <Button size="sm" variant="ghost" leftIcon={<Play />} onClick={() => setRun((r) => r + 1)}>
          проиграть
        </Button>
      </div>
    </div>
  );
}

function NoteDemo() {
  const [run, setRun] = useState(0);
  return (
    <div className="flex flex-col gap-3">
      <Stage className="flex justify-center py-8">
        <KanameNote key={run} animate={run > 0} className="max-w-xl text-center" />
      </Stage>
      <div>
        <Button size="sm" variant="ghost" leftIcon={<Play />} onClick={() => setRun((r) => r + 1)}>
          проиграть
        </Button>
      </div>
    </div>
  );
}

const GLYPH_SIZES = ['size-4', 'size-5', 'size-6', 'size-8'] as const;

function GlyphDemo() {
  return (
    <div className="flex flex-wrap items-center gap-8">
      <div className="flex items-end gap-4 text-fg">
        {GLYPH_SIZES.map((s) => (
          <div key={s} className="flex flex-col items-center gap-2">
            <KanameGlyph className={s} />
            <Code>{s}</Code>
          </div>
        ))}
      </div>
      {/* Неполный значок — для пустоты (центр без сервисов): бледные гнёзда на месте лепестков. */}
      <div className="flex items-end gap-6 text-fg">
        <div className="flex flex-col items-center gap-2">
          <KanameGlyph petals={0} seats core="ring" className="size-8" />
          <Code>{'petals={0} seats core="ring"'}</Code>
        </div>
        <div className="flex flex-col items-center gap-2">
          <KanameGlyph petals={2} seats className="size-8" />
          <Code>{'petals={2} seats'}</Code>
        </div>
        <div className="flex flex-col items-center gap-2">
          <KanameGlyph petals={2} className="size-8" />
          <Code>{'petals={2}'}</Code>
        </div>
      </div>
      <div className="flex w-56 flex-col gap-1">
        <span className="inline-flex h-9 items-center gap-2 rounded-sm px-3 text-sm text-fg-muted">
          <KanameGlyph className="size-4" />О Kaname
        </span>
        <span className="inline-flex h-9 items-center gap-2 rounded-sm bg-brand-soft px-3 text-sm font-medium text-brand-fg">
          <KanameGlyph className="size-4" />О Kaname
        </span>
      </div>
    </div>
  );
}

function IntroDemo() {
  // «Заново» — перемонтирование: ядро снова ждёт касания, как при открытии страницы.
  const [run, setRun] = useState(0);
  const [phase, setPhase] = useState<IntroPhase>('core');
  return (
    <div className="flex flex-col gap-3">
      <Stage className="grid gap-6 sm:grid-cols-2">
        <Sample code='mode="still"'>
          <KanameIntro mode="still" className="size-40" />
        </Sample>
        <Sample code={`mode="first" · ${phase}`}>
          <KanameIntro key={run} mode="first" onPhaseChange={setPhase} className="size-40" />
        </Sample>
      </Stage>
      <div className="flex flex-wrap items-center gap-3">
        <Button size="sm" variant="ghost" leftIcon={<RotateCcw />} onClick={() => setRun((r) => r + 1)}>
          заново
        </Button>
        <span className="text-xs text-fg-muted">
          Коснись ядра — веер раскроется; не коснёшься — раскроется сам через 2,6 с. Фазы: core → bloom →
          depth → settled.
        </span>
      </div>
    </div>
  );
}

function LeafGroundDemo() {
  const stageRef = useRef<HTMLDivElement>(null);
  const markRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(true);
  return (
    <div className="flex flex-col gap-3">
      <div
        ref={stageRef}
        className="relative flex h-72 justify-center overflow-hidden rounded-md bg-night pt-8"
      >
        <LeafGround stageRef={stageRef} markRef={markRef} active={active} />
        <div ref={markRef} className="size-28">
          <KanameMark variant="window" leaves className="size-full" />
        </div>
      </div>
      <Switch
        label="Листья выпадают"
        description="Раз в 2–3 с лист выпадает из нижнего окна и ложится внизу; на земле не больше 30. Веди мышью по земле — листья разлетаются. При уменьшенном движении листопада на землю нет."
        checked={active}
        onCheckedChange={setActive}
      />
    </div>
  );
}

function GustDemo() {
  const { gust, tap } = useGust();
  return (
    <div className="flex flex-wrap items-center gap-6">
      <Stage className="grid place-items-center p-8">
        <button
          type="button"
          aria-label="Знак: три быстрых нажатия — порыв ветра"
          onClick={tap}
          className="rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <KanameMark life="breath" breathing gust={gust} className="size-24" />
        </button>
      </Stage>
      <div className="flex max-w-sm flex-col gap-1">
        <p className="text-sm text-fg">Нажми на знак трижды подряд — быстро, за 1,2 с.</p>
        <p className="text-xs text-fg-muted">
          Порыв ветра раскручивает веер на три оборота; он затихает и замирает ровно в исходном виде. Нажатия
          на ходу подхватывают веер с полной скоростью. При уменьшенном движении веер неподвижен. Порывов:{' '}
          {gust}.
        </p>
      </div>
    </div>
  );
}

function MenuCoreDemo() {
  return (
    <div className="flex flex-wrap items-center gap-6">
      <div className="flex items-center gap-8 rounded-md bg-rail px-8 py-5 text-rail-fg">
        <Sample code="на главной">
          <span className="relative grid size-12 place-items-center">
            <span aria-hidden className="absolute inset-0 rounded-full brand-halo" />
            <KanameMark breathing className="relative size-10" />
          </span>
        </Sample>
        <Sample code='core="away"'>
          <span className="grid size-12 place-items-center">
            <KanameMark core="away" className="size-10" />
          </span>
        </Sample>
      </div>
      <div className="flex max-w-md flex-col items-start gap-2">
        <p className="text-sm text-fg-muted">
          Знак в главном меню — кнопка «Главная». Уходишь с главной — ядро каплей вытекает из знака и
          растекается в выделение на пункте, в знаке остаётся тонкое кольцо; возвращаешься — выделение
          стягивается домой. Долгое нажатие заново раскрывает веер. Полёт живёт только в настоящем меню.
        </p>
        <Link
          to="/design/foundation"
          className="inline-flex h-9 items-center gap-1.5 rounded-md px-3 text-xs font-medium text-brand-fg transition-colors duration-fast hover:bg-brand-soft"
        >
          Посмотреть в «Основе»
          <ArrowRight className="size-3.5" />
        </Link>
      </div>
    </div>
  );
}

/* ---------- Дом помощника (R36, spec-3 §2–§3): ядро в знаке и выделение пункта ---------- */

/*
  Дом помощника — ядро знака на главной и выделение пункта первого уровня в разделе. Отклики дома (тон,
  ритм ожидания, жест, кольцо и контур в отлучке) — помощника: в «Основе» их ведёт провайдер
  (ObserverProvider home="core"), без него знак и меню прежние (решение 21.10). Здесь те же утилиты и
  атрибуты, что ставит MainMenu, на неподвижных образцах; жесты читаются из EVENT_PLAN — сменишь их там,
  сменятся и тут.
*/

/** Тёмная полоса меню: на ней знак и пункты, как в главном меню. Жесты дома — только внутри data-slot. */
function RailStage({ children }: { children: ReactNode }) {
  return (
    <div
      data-slot="observer"
      className="flex flex-1 flex-wrap items-start justify-center gap-6 rounded-md bg-rail px-6 py-5 text-rail-fg"
    >
      {children}
    </div>
  );
}

/** Знак дома, как в меню на главной: свечение берёт тон ядра, «ждёт ответа» — двойной удар. */
function HomeMark({
  tone = 'brand',
  rhythm = 'calm',
  core = 'home',
  cue = null,
}: {
  tone?: MarkTone;
  rhythm?: MarkRhythm;
  core?: 'home' | 'away';
  cue?: MarkCue | null;
}) {
  return (
    <span className="relative grid size-12 place-items-center">
      <span
        aria-hidden
        data-tone={tone === 'brand' ? undefined : tone}
        data-await={rhythm === 'await' || undefined}
        className={cn(
          'absolute inset-0 rounded-full core-tone transition-opacity duration-smooth brand-halo',
          core === 'home' ? 'opacity-100' : 'opacity-0',
        )}
      />
      <KanameMark
        core={core}
        breathing
        life="breath"
        tone={tone}
        rhythm={rhythm}
        cue={cue}
        className="relative size-10"
      />
    </span>
  );
}

/**
 * Пункт главного меню с выделением: свёрнутое меню — только значок, раскрытое — с подписью. away — ядро у
 * события: выделения нет, на пункте контур (data-core-away). Жест играет заново при каждой смене run.
 */
function PillItem({
  label,
  wide = false,
  tone = 'brand',
  waiting = false,
  away = false,
  gesture = null,
  run = 0,
}: {
  label: string;
  wide?: boolean;
  tone?: MarkTone;
  waiting?: boolean;
  away?: boolean | 'out';
  gesture?: HomeGesture | null;
  run?: number;
}) {
  const pill = useRef<HTMLSpanElement>(null);
  // Как у MainMenu: атрибут снимается и ставится после пересчёта стилей — тот же жест подряд играет заново.
  useEffect(() => {
    const el = pill.current;
    if (!el || run === 0 || !gesture) return undefined;
    const g = gesture === 'burst' ? 'pulse' : gesture;
    el.removeAttribute('data-gesture');
    void getComputedStyle(el).getPropertyValue('animation-name');
    el.setAttribute('data-gesture', g);
    const t = window.setTimeout(() => el.removeAttribute('data-gesture'), HOME_GESTURE_MS[g]);
    return () => window.clearTimeout(t);
  }, [run, gesture]);
  return (
    <span
      data-core-away={away === 'out' ? 'out' : away ? '' : undefined}
      className={cn(
        'relative flex h-12 shrink-0 items-center rounded-md text-on-brand',
        wide ? 'w-48' : 'w-12',
      )}
    >
      <span
        ref={pill}
        aria-hidden
        data-ready=""
        data-tone={tone === 'brand' ? undefined : tone}
        data-await={waiting || undefined}
        className={cn('rail-pill absolute inset-0 rounded-md state-tone', away && 'opacity-0')}
      />
      <span className="relative grid size-12 shrink-0 place-items-center">
        <Activity className="size-5" aria-label={wide ? undefined : label} />
      </span>
      {wide && <span className="relative ml-1 text-sm font-medium">{label}</span>}
    </span>
  );
}

/** Знак над пунктом: дом на главной и дом в разделе одним столбиком, подпись-код снизу. */
function HomePair({ code, note, children }: { code: string; note?: string; children: ReactNode }) {
  return (
    <Sample code={code} note={note}>
      <span className="flex flex-col items-center gap-3">{children}</span>
    </Sample>
  );
}

/** Подблок строки «Дом помощника»: заголовок, «проиграть» (если есть), сцена меню, пояснение. */
function HomeBlock({
  title,
  onPlay,
  note,
  wide = false,
  children,
}: {
  title: string;
  onPlay?: () => void;
  note: ReactNode;
  /** На всю ширину строки: много образцов в ряд. */
  wide?: boolean;
  children: ReactNode;
}) {
  return (
    <div className={cn('flex flex-col gap-3', wide && 'lg:col-span-2')}>
      <div className="flex min-h-9 flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-medium text-fg">{title}</span>
        {onPlay && (
          <Button size="sm" variant="ghost" leftIcon={<Play />} onClick={onPlay}>
            проиграть
          </Button>
        )}
      </div>
      <RailStage>{children}</RailStage>
      <span className="text-xs text-fg-muted">{note}</span>
    </div>
  );
}

const HOME_TONES: { value: MarkTone; label: string }[] = [
  { value: 'brand', label: 'покой' },
  { value: 'info', label: 'операция' },
  { value: 'success', label: 'готово' },
  { value: 'danger', label: 'сбой' },
];

/** «Проиграть» стопку дома: операция → вспышка «готово» (2,6 с) → покой → сбой → покой. */
const TONE_RUN: { tone: MarkTone; label: string; ms: number }[] = [
  { tone: 'info', label: 'операция · 320 мс', ms: 1600 },
  { tone: 'success', label: 'готово · 120 мс', ms: SUCCESS_HOLD_MS },
  { tone: 'brand', label: 'покой · 800 мс', ms: 1400 },
  { tone: 'danger', label: 'сбой · скачком', ms: 1600 },
  { tone: 'brand', label: 'покой · 800 мс', ms: 0 },
];

function HomeToneDemo() {
  const [step, setStep] = useState(-1);
  useEffect(() => {
    if (step < 0 || step >= TONE_RUN.length - 1) return undefined;
    const t = window.setTimeout(() => setStep((s) => s + 1), TONE_RUN[step].ms);
    return () => window.clearTimeout(t);
  }, [step]);
  const live = step < 0 ? null : TONE_RUN[step];
  const tone = live?.tone ?? 'brand';
  return (
    <HomeBlock
      title="Ядро: тон состояния"
      wide
      onPlay={() => setStep(0)}
      note="Цвет — по стопке состояний: сбой > ждёт ответа > операция > покой; «готово» — вспышка поверх неё на 2,6 с. Темп несёт срочность. «Окраска по состоянию» выключена — дом всегда коралловый."
    >
      <HomePair code={live ? live.label : 'живой образец'}>
        <HomeMark tone={tone} />
        <PillItem label="Marketplace" tone={tone} />
      </HomePair>
      <span aria-hidden className="w-px self-stretch bg-rail-line" />
      {HOME_TONES.map((t) => (
        <HomePair key={t.value} code={`tone="${t.value}"`} note={t.label}>
          <HomeMark tone={t.value} />
          <PillItem label="Marketplace" tone={t.value} />
        </HomePair>
      ))}
    </HomeBlock>
  );
}

function HomeAwaitDemo() {
  return (
    <HomeBlock
      title="Ядро ждёт ответа"
      note="Двойной удар в такт веера: ×1,10 и ×1,06 за 3,6 с, дальше покой. Цвет остаётся коралловым — смысл несёт ритм (один коралл на экране). На выделении дышит только свечение; без движения — ровное свечение."
    >
      <HomePair code={'rhythm="await"'} note="знак на главной">
        <HomeMark rhythm="await" />
      </HomePair>
      <HomePair code="data-await" note="выделение в разделе">
        <PillItem label="Инфраструктура" waiting />
        <PillItem label="Инфраструктура" wide waiting />
      </HomePair>
    </HomeBlock>
  );
}

/** Отлучка ядра: дома → ушло → возвращается; контур проявляется за 200 мс и гаснет за 160. */
type AwayPhase = 'home' | 'away' | 'out';

function HomeAwayDemo() {
  const [phase, setPhase] = useState<AwayPhase>('home');
  useEffect(() => {
    if (phase === 'home') return undefined;
    const t = window.setTimeout(
      () => setPhase(phase === 'away' ? 'out' : 'home'),
      phase === 'away' ? 2000 : 160,
    );
    return () => window.clearTimeout(t);
  }, [phase]);
  const gone = phase !== 'home';
  return (
    <HomeBlock
      title="Ядро ушло: кольцо в знаке и контур пункта"
      wide
      onPlay={() => setPhase('away')}
      note="Пока ядро у события, место «где я» не пропадает: в знаке белое кольцо, на пункте контур 1,5 px; подпись и aria-current пункта не меняются. Колец-подсветок на пунктах нет — указывает сама капля."
    >
      <HomePair code={gone ? 'core="away"' : 'core="home"'} note="живой: ушло на 2 с">
        <HomeMark core={gone ? 'away' : 'home'} />
        <PillItem label="Marketplace" away={phase === 'out' ? 'out' : gone} />
      </HomePair>
      <span aria-hidden className="w-px self-stretch bg-rail-line" />
      <HomePair code={'core="away"'} note="знак на главной">
        <HomeMark core="away" />
      </HomePair>
      <HomePair code="data-core-away" note="пункт в разделе">
        <PillItem label="Marketplace" away />
        <PillItem label="Marketplace" wide away />
      </HomePair>
    </HomeBlock>
  );
}

/** Отклики дома по плану событий: что играет ядро в знаке и выделение пункта. */
const HOME_REACTIONS: {
  kind: 'notification' | 'done' | 'error';
  label: string;
  tone: MarkTone;
  ms: number;
}[] = [
  { kind: 'notification', label: 'уведомление', tone: 'brand', ms: 0 },
  { kind: 'done', label: 'готово', tone: 'success', ms: SUCCESS_HOLD_MS },
  { kind: 'error', label: 'сбой', tone: 'danger', ms: 1600 },
];

function HomeCueDemo() {
  const [run, setRun] = useState(0);
  const [toned, setToned] = useState<Record<string, boolean>>({});
  useEffect(() => {
    if (run === 0) return undefined;
    const timers = HOME_REACTIONS.filter((r) => r.ms > 0).map((r) =>
      window.setTimeout(() => setToned((t) => ({ ...t, [r.kind]: false })), r.ms),
    );
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [run]);
  const play = () => {
    setToned({ done: true, error: true });
    setRun((n) => n + 1);
  };
  return (
    <HomeBlock
      title="Отклик дома"
      onPlay={play}
      note="Жест — из EVENT_PLAN: уведомление — кольцо вокруг ядра и пульс выделения; «готово» — вспышка и зелёный на 2,6 с; сбой — дрожь ядра, у выделения только скачок цвета (дрожь широкой таблетки задела бы соседей)."
    >
      {HOME_REACTIONS.map((r) => {
        const plan = EVENT_PLAN[r.kind];
        const tone = toned[r.kind] ? r.tone : 'brand';
        const mark = plan.mark;
        return (
          <HomePair key={r.kind} code={`${mark ?? '—'} · ${plan.pill ?? 'цвет'}`} note={r.label}>
            <HomeMark tone={tone} cue={run > 0 && mark ? { gesture: mark, at: run } : null} />
            <PillItem label="Marketplace" tone={tone} gesture={plan.pill} run={run} />
          </HomePair>
        );
      })}
    </HomeBlock>
  );
}

function AssistantHomeDemo() {
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <HomeToneDemo />
        <HomeCueDemo />
        <HomeAwaitDemo />
        <HomeAwayDemo />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <p className="max-w-2xl text-sm text-fg-muted">
          Дом помощника — ядро знака на главной и выделение пункта первого уровня в разделе (R36). Отклики
          дома ведёт помощник; без него знак и меню прежние. Полёт ядра к событию — плитка в разделе Motion,
          события в работе — в «Основе», ряд «Помощник».
        </p>
        <Link
          to="/design/foundation"
          className="inline-flex h-9 items-center gap-1.5 rounded-md px-3 text-xs font-medium text-brand-fg transition-colors duration-fast hover:bg-brand-soft"
        >
          Посмотреть в «Основе»
          <ArrowRight className="size-3.5" />
        </Link>
      </div>
    </div>
  );
}

const SIZES: AvatarSize[] = ['xs', 'sm', 'md', 'lg', 'xl'];
const NOOP = () => undefined;
const DEMO_ACCOUNT: AccountInfo = {
  name: 'Демо Пользователь',
  email: 'demo@example.test',
  onProfile: NOOP,
  onAccountSettings: NOOP,
  onLogout: NOOP,
};

/** «Я» внизу меню: заглушка — знак контуром с пустым ядром, строка аккаунта и окно, ручка на кромке. */
/** Образец на обычной карточке: подпись цветом темы (Sample — для тёмной сцены). */
function CardSample({ code, children }: { code: string; children: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-3">
      {children}
      <code className="text-center font-mono text-2xs text-fg-muted">{code}</code>
    </div>
  );
}

function AccountDemo() {
  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end gap-6">
        <div className="flex items-end gap-3">
          {SIZES.map((s) => (
            <CardSample key={s} code={s}>
              <Avatar size={s} name="Демо Пользователь" fallback="mark" />
            </CardSample>
          ))}
        </div>
        <div className="flex items-end gap-3 rounded-md bg-rail px-4 py-3 text-rail-fg-muted">
          {SIZES.map((s) => (
            <Avatar key={s} size={s} name="Демо Пользователь" fallback="mark" tone="rail" />
          ))}
          <Avatar size="md" name="Демо Пользователь" fallback="mark" tone="rail" turned />
        </div>
        <CardSample code="фото не загрузилось → знак">
          <Avatar size="lg" name="Демо Пользователь" fallback="mark" src="/__нет-такого-фото.png" />
        </CardSample>
      </div>
      <div className="flex flex-wrap items-end gap-6">
        <div className="w-60 rounded-lg px-4 py-3 rail-surface">
          <AccountMenu account={DEMO_ACCOUNT} expanded />
        </div>
        <div className="w-20 rounded-lg px-4 py-3 rail-surface">
          <AccountMenu account={DEMO_ACCOUNT} expanded={false} />
        </div>
        <p className="max-w-sm text-sm text-fg-muted">
          Нажми строку — окно аккаунта выходит справа: профиль, настройки аккаунта, тема, выйти. Окно — тёмный
          остров в обеих темах. Ручка сворачивания — на правой кромке меню у знака: волосок в покое, капсула
          со стрелкой под курсором, клавиша «[»; живёт только в настоящем меню.
        </p>
      </div>
    </div>
  );
}

export function BrandSection() {
  return (
    <Section
      id="brand"
      title="Знак и бренд"
      lead="Один центр, множество элементов (R41): лепестки твёрдые — только поворот и прозрачность, жидкое — только ядро. Лепестки светлые, поэтому знак показан на тёмной сцене в обеих темах; значок меню и слово в строке берут цвет от текста."
    >
      <Row
        title="KanameMark — знак"
        note={<Code>open · core · breathing · variant · leaves · tempo · life · gust</Code>}
      >
        <Stage className="grid grid-cols-3 gap-6 sm:grid-cols-6">
          {STATES.map((s) => (
            <Sample key={s.code} code={s.code}>
              <KanameMark {...s.props} className="size-16" />
            </Sample>
          ))}
        </Stage>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-3">
            <Stage className="grid flex-1 grid-cols-3 content-center gap-6">
              {LIVES.map((s) => (
                <Sample key={s.code} code={s.code}>
                  <KanameMark {...s.props} className="size-16" />
                </Sample>
              ))}
            </Stage>
            <span className="text-xs text-fg-muted">
              Живёт сам: ядро и веер дышат по общим часам, поворот — раз в 12 с.
            </span>
          </div>
          <MarkPlay />
        </div>
      </Row>

      <Row title="Состояния знака" note={<Code>petals · reveal · tone · rhythm · cue</Code>}>
        <MarkStatesDemo />
      </Row>

      <Row
        title="Состояния знака — ожидание и ошибки"
        note={<Code>StateMark kind · done · total · setting · core · alarm — словарь MARK_STATES</Code>}
      >
        <StateGrid />
        <StatePlayer />
        <WhereTable />
      </Row>

      <Row
        title="ServiceFan — веер сервисов"
        note={
          <>
            <Code>plates · activeId · pinnedId · onPin · interactive · bloom</Code> Пластина на сервис в
            порядке подключения с 6 часов: светлая — включён, контур — выключен, красная — сбой. До шести —
            кадры знака, дальше шаг 360/N, следующая поверх предыдущей. Соседи раздвигаются поворотом,
            остальные притухают цветом, выбранная отходит от заклёпки; свет наливается от заклёпки. Живое ядро
            — только в меню.
          </>
        }
      >
        <FanSamples />
        <FanDemo />
      </Row>

      <Row
        title="KanameWordmark — слово"
        note={<Code>size="stage" | "inline" · play="hidden" | "play" | "still"</Code>}
      >
        <WordmarkDemo />
      </Row>

      <Row title="KanameNote — сноска" note={<Code>animate · только на тёмной сцене</Code>}>
        <NoteDemo />
      </Row>

      <Row title="KanameGlyph — значок меню" note={<Code>currentColor · знак линией</Code>}>
        <GlyphDemo />
      </Row>

      <Row
        title="KanameIntro — знакомство на входе"
        note={<Code>mode="first" | "still" · onPhaseChange · KanameIntroHandle</Code>}
      >
        <IntroDemo />
      </Row>

      <Row title="LeafGround — листья на земле" note={<Code>stageRef · markRef · active</Code>}>
        <LeafGroundDemo />
      </Row>

      <Row
        title="useGust — порыв ветра"
        note={<Code>{'const { gust, tap } = useGust() · KanameMark gust'}</Code>}
      >
        <GustDemo />
      </Row>

      <Row
        title="Аккаунт внизу меню — «я»"
        note={
          <Code>
            {'Avatar fallback="mark" tone="rail" turned · AccountMenu · DropdownMenuContent tone="rail"'}
          </Code>
        }
      >
        <AccountDemo />
      </Row>

      <Row
        title="MainMenu — ядро и выделение"
        note={<Code>MainMenu · BrandHome · KanameMark core life gust</Code>}
      >
        <MenuCoreDemo />
      </Row>

      <Row
        title="Дом помощника — ядро и выделение"
        note={
          <Code>
            {
              'MainMenu в ObserverProvider home="core" · tone · rhythm="await" · cue · core="away" · rail-pill data-tone · data-await · data-gesture · data-core-away'
            }
          </Code>
        }
      >
        <AssistantHomeDemo />
      </Row>
    </Section>
  );
}
